import React from 'react';
import { i18n } from 'config/locales';
import { ForeignTravelItemModel } from 'forms/sections/foreign/travel/types';
import {
  CommonOnUpdate, CommonRequired, CommonOnError, CommonScrollIntoView
} from 'types/props';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import Field from 'components/Form/Field';
import Country from 'components/Form/Country';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Textarea from 'components/Form/Textarea';
import DateRange from 'components/Form/DateRange';
import * as formTypes from 'constants/formTypes';
import { isPVQForm } from 'helpers/form';
import TravelPurpose from './TravelPurpose';
import TravelDays from './TravelDays';
import TravelDaysPVQ from './TravelDaysPvq';
import { FieldModel } from 'forms/shared/types';

interface Props extends Partial<ForeignTravelItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  required?: CommonRequired
  requireForeignCounterIntelligence?: boolean
  requireForeignExcessiveKnowledge?: boolean
  requireForeignSensitiveInformation?: boolean
  requireForeignThreatened?: boolean
  onError?: CommonOnError
  scrollIntoView?: CommonScrollIntoView
  formType: formTypes.FormTypes
}

export default class TravelQuestions extends ValidationElement<Props> {
  constructor(props) {
    super(props);
    this.updateCountryFocused = this.updateCountryFocused.bind(this);
  }

  update = (queue: Partial<ForeignTravelItemModel>) => {
    this.props.onUpdate({
      Dates: this.props.Dates,
      Country: this.props.Country,
      Days: this.props.Days,
      Purpose: this.props.Purpose,
      Questioned: this.props.Questioned,
      QuestionedExplanation: this.props.QuestionedExplanation,
      PurposeExplanation: this.props.PurposeExplanation,
      Encounter: this.props.Encounter,
      EncounterExplanation: this.props.EncounterExplanation,
      Contacted: this.props.Contacted,
      ContactedExplanation: this.props.ContactedExplanation,
      Counter: this.props.Counter,
      CounterExplanation: this.props.CounterExplanation,
      Interest: this.props.Interest,
      InterestExplanation: this.props.InterestExplanation,
      Sensitive: this.props.Sensitive,
      SensitiveExplanation: this.props.SensitiveExplanation,
      Threatened: this.props.Threatened,
      ThreatenedExplanation: this.props.ThreatenedExplanation,
      CountryFocused: this.props.CountryFocused,
      ...queue,
    });
  };

  updateThreatenedExplanation = (values: ForeignTravelItemModel['ThreatenedExplanation']) => {
    this.update({
      ThreatenedExplanation: values,
    });
  };

  updateThreatened = (values: ForeignTravelItemModel['Threatened']) => {
    if (values.value === 'No') {
      this.update({
        Threatened: values,
        ThreatenedExplanation: {} as FieldModel,
      });
    } else {
      this.update({
        Threatened: values,
      });
    }
  };

  updateSensitiveExplanation = (values: ForeignTravelItemModel['SensitiveExplanation']) => {
    this.update({
      SensitiveExplanation: values,
    });
  };

  updateSensitive = (values: ForeignTravelItemModel['Sensitive']) => {
    if (values.value === 'No') {
      this.update({
        Sensitive: values,
        SensitiveExplanation: {} as FieldModel,
      });
    } else {
      this.update({
        Sensitive: values,
      });
    }
  };

  updateInterestExplanation = (values: ForeignTravelItemModel['InterestExplanation']) => {
    this.update({
      InterestExplanation: values,
    });
  };

  updateInterest = (values: ForeignTravelItemModel['Interest']) => {
    if (values.value === 'No') {
      this.update({
        Interest: values,
        InterestExplanation: {} as FieldModel,
      });
    } else {
      this.update({
        Interest: values,
      });
    }
  };

  updateCounterExplanation = (values: ForeignTravelItemModel['CounterExplanation']) => {
    this.update({
      CounterExplanation: values,
    });
  };

  updateCounter = (values: ForeignTravelItemModel['Counter']) => {
    if (values.value === 'No') {
      this.update({
        Counter: values,
        CounterExplanation: {} as FieldModel,
      });
    } else {
      this.update({
        Counter: values,
      });
    }
  };

  updateContactedExplanation = (values: ForeignTravelItemModel['ContactedExplanation']) => {
    this.update({
      ContactedExplanation: values,
    });
  };

  updateContacted = (values: ForeignTravelItemModel['Contacted']) => {
    if (values.value === 'No') {
      this.update({
        Contacted: values,
        ContactedExplanation: {} as FieldModel,
      });
    } else {
      this.update({
        Contacted: values,
      });
    }
  };

  updateEncounterExplanation = (values: ForeignTravelItemModel['EncounterExplanation']) => {
    this.update({
      EncounterExplanation: values,
    });
  };

  updateEncounter = (values: ForeignTravelItemModel['Encounter']) => {
    if (values.value === 'No') {
      this.update({
        Encounter: values,
        EncounterExplanation: {} as FieldModel,
      });
    } else {
      this.update({
        Encounter: values,
      });
    }
  };

  updateQuestionedExplanation = (values: ForeignTravelItemModel['QuestionedExplanation']) => {
    this.update({
      QuestionedExplanation: values,
    });
  };

  updatePurposeExplanation = (values: ForeignTravelItemModel['PurposeExplanation']) => {
    this.update({
      PurposeExplanation: values,
    });
  };

  updateQuestioned = (values: ForeignTravelItemModel['Questioned']) => {
    if (values.value === 'No') {
      this.update({
        Questioned: values,
        QuestionedExplanation: {} as FieldModel,
      });
    } else {
      this.update({
        Questioned: values,
      });
    }
  };

  updatePurpose = (values: ForeignTravelItemModel['Purpose']) => {
    this.update({
      Purpose: values,
    });
  };

  updateDays = (values: ForeignTravelItemModel['Days']) => {
    this.update({
      Days: values,
    });
  };

  updateDates = (values: ForeignTravelItemModel['Dates']) => {
    this.update({
      Dates: values,
    });
  };

  updateCountry = (values: ForeignTravelItemModel['Country']) => {
    this.update({
      Country: values,
    });
  };

  updateCountryFocused(focus: boolean) {
    this.update({
      CountryFocused: focus,
    });
  }

  render() {
    const {
      requireForeignCounterIntelligence,
      requireForeignExcessiveKnowledge,
      requireForeignSensitiveInformation,
      requireForeignThreatened,
    } = this.props;

    const attribute = `List.items[${this.props.$index}].Item`;
    const isPvq = isPVQForm(this.props.formType);
    const isOther = Array.isArray(this.props.Purpose?.values) && this.props.Purpose.values.includes('Other');
    return isPvq ? (
      <div className="foreign-travel-questions">
        <Field
          attribute={`${attribute}.Country`}
          title={i18n.t('foreign.travel_pvq.heading.country')}
          adjustFor="country"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Country
            attribute={`${attribute}.Country`}
            name="Country"
            {...this.props.Country}
            excludeUnitedStates
            className="foreign-travel-country"
            onUpdate={this.updateCountry}
            updateCountryFocused={this.updateCountryFocused}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('foreign.travel_pvq.heading.dates')}
          help="foreign.travel_pvq.help.dates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Dates`}
        >
          <DateRange
            attribute={`${attribute}.Dates`}
            name="Dates"
            {...this.props.Dates}
            className="foreign-travel-dates"
            minDateEqualTo
            onUpdate={this.updateDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Field
          attribute={`${attribute}.Days`}
          title={i18n.t('foreign.travel_pvq.heading.days')}
          help="foreign.travel_pvq.help.days"
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
        >
          <TravelDaysPVQ
            attribute={`${attribute}.Days`}
            name="Days"
            className="foreign-travel-days"
            {...this.props.Days}
            onUpdate={this.updateDays}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Field
          attribute={`${attribute}.Purpose`}
          title={i18n.t('foreign.travel_pvq.heading.purpose')}
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
        >
          {i18n.m('foreign.travel_pvq.para.checkall')}
          <TravelPurpose
            attribute={`${attribute}.Purpose`}
            name="Purpose"
            className="foreign-travel-purpose"
            {...this.props.Purpose}
            onUpdate={this.updatePurpose}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        {isOther && (
          <Field
            attribute={`${attribute}.PurposeExplanation`}
            title={i18n.t('foreign.travel_pvq.heading.explanationOther')}
            adjustFor="p"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              attribute={`${attribute}.PurposeExplanation`}
              name="PurposeExplanation"
              {...this.props.PurposeExplanation}
              className="foreign-travel-purpose-explanation"
              onUpdate={this.updatePurposeExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        <Branch
          attribute={`${attribute}.Questioned`}
          label={i18n.t('foreign.travel_pvq.heading.questioned')}
          labelSize="h4"
          name="has_foreign_travel_questioned"
          className={`foreign-travel-questioned ${(this.props.Questioned || {}).value === 'Yes' ? 'no-margin-bottom' : ''
            }`}
          {...this.props.Questioned}
          onUpdate={this.updateQuestioned}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Show when={(this.props.Questioned || {}).value === 'Yes'}>
          <Field
            attribute={`${attribute}.QuestionedExplanation`}
            title={i18n.t('foreign.travel_pvq.heading.explanation')}
            titleSize="label"
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              attribute={`${attribute}.QuestionedExplanation`}
              name="QuestionedExplanation"
              {...this.props.QuestionedExplanation}
              className="foreign-travel-questioned-explanation"
              onUpdate={this.updateQuestionedExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>

        <Branch
          attribute={`${attribute}.Encounter`}
          label={i18n.t('foreign.travel_pvq.heading.encounter')}
          labelSize="h4"
          name="has_foreign_travel_encounter"
          className={`foreign-travel-encounter ${this.props.Encounter.value === 'Yes' ? 'no-margin-bottom' : ''
            }`}
          {...this.props.Encounter}
          onUpdate={this.updateEncounter}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Show when={(this.props.Encounter || {}).value === 'Yes'}>
          <Field
            attribute={`${attribute}.EncounterExplanation`}
            title={i18n.t('foreign.travel_pvq.heading.explanation')}
            titleSize="label"
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              attribute={`${attribute}.EncounterExplanation`}
              name="EncounterExplanation"
              {...this.props.EncounterExplanation}
              className="foreign-travel-encounter-explanation"
              onUpdate={this.updateEncounterExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>

        <Branch
          attribute={`${attribute}.Contacted`}
          label={i18n.t('foreign.travel_pvq.heading.contacted')}
          labelSize="h4"
          name="has_foreign_travel_contacted"
          className={`foreign-travel-contacted ${this.props.Contacted.value === 'Yes' ? 'no-margin-bottom' : ''
            }`}
          {...this.props.Contacted}
          onUpdate={this.updateContacted}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Show when={(this.props.Contacted || {}).value === 'Yes'}>
          <Field
            attribute={`${attribute}.ContactedExplanation`}
            title={i18n.t('foreign.travel_pvq.heading.explanation')}
            titleSize="label"
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              name="ContactedExplanation"
              attribute={`${attribute}.ContactedExplanation`}
              {...this.props.ContactedExplanation}
              className="foreign-travel-contacted-explanation"
              onUpdate={this.updateContactedExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>

        <Branch
          attribute={`${attribute}.Counter`}
          label={i18n.t('foreign.travel_pvq.heading.counter')}
          labelSize="h4"
          name="has_foreign_travel_counter"
          className={`foreign-travel-counter ${this.props.Counter.value === 'Yes' ? 'no-margin-bottom' : ''
            }`}
          {...this.props.Counter}
          onUpdate={this.updateCounter}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Show when={(this.props.Counter || {}).value === 'Yes'}>
          <Field
            attribute={`${attribute}.CounterExplanation`}
            title={i18n.t('foreign.travel_pvq.heading.explanation')}
            titleSize="label"
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              name="CounterExplanation"
              attribute={`${attribute}.CounterExplanation`}
              {...this.props.CounterExplanation}
              className="foreign-travel-counter-explanation"
              onUpdate={this.updateCounterExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>



        <Branch
          attribute={`${attribute}.Interest`}
          label={i18n.t('foreign.travel_pvq.heading.interest')}
          labelSize="h4"
          name="has_foreign_travel_interest"
          className={`foreign-travel-interest ${this.props.Interest.value === 'Yes' ? 'no-margin-bottom' : ''
            }`}
          {...this.props.Interest}
          onUpdate={this.updateInterest}
          required={this.props.required}
          onError={this.props.onError}
        />

        <Show when={(this.props.Interest || {}).value === 'Yes'}>
          <Field
            attribute={`${attribute}.InterestExplanation`}
            title={i18n.t('foreign.travel_pvq.heading.explanation')}
            titleSize="label"
            adjustFor="textarea"
          >
            <Textarea
              attribute={`${attribute}.InterestExplanation`}
              name="InterestExplanation"
              {...this.props.InterestExplanation}
              className="foreign-travel-interest-explanation"
              onUpdate={this.updateInterestExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>

        <Branch
          attribute={`${attribute}.Sensitive`}
          label={i18n.t('foreign.travel_pvq.heading.sensitive')}
          labelSize="h4"
          name="has_foreign_travel_sensitive"
          className={`foreign-travel-sensitive ${this.props.Sensitive.value === 'Yes' ? 'no-margin-bottom' : ''
            }`}
          {...this.props.Sensitive}
          onUpdate={this.updateSensitive}
          required={this.props.required}
          onError={this.props.onError}
        />

        <Show when={(this.props.Sensitive || {}).value === 'Yes'}>
          <Field
            attribute={`${attribute}.SensitiveExplanation`}
            title={i18n.t('foreign.travel_pvq.heading.explanation')}
            titleSize="label"
            adjustFor="textarea"
          >
            <Textarea
              attribute={`${attribute}.SensitiveExplanation`}
              name="SensitiveExplanation"
              {...this.props.SensitiveExplanation}
              className="foreign-travel-sensitive-explanation"
              onUpdate={this.updateSensitiveExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>

        <Branch
          attribute={`${attribute}.Threatened`}
          label={i18n.t('foreign.travel_pvq.heading.threatened')}
          labelSize="h4"
          name="has_foreign_travel_threatened"
          className={`foreign-travel-threatened ${this.props.Threatened.value === 'Yes' ? 'no-margin-bottom' : ''
            }`}
          {...this.props.Threatened}
          onUpdate={this.updateThreatened}
          required={this.props.required}
          onError={this.props.onError}
        />

        <Show when={(this.props.Threatened || {}).value === 'Yes'}>
          <Field
            attribute={`${attribute}.ThreatenedExplanation`}
            title={i18n.t('foreign.travel_pvq.heading.explanation')}
            titleSize="label"
            adjustFor="textarea"
          >
            <Textarea
              attribute={`${attribute}.ThreatenedExplanation`}
              name="ThreatenedExplanation"
              {...this.props.ThreatenedExplanation}
              className="foreign-travel-threatened-explanation"
              onUpdate={this.updateThreatenedExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>

      </div>
    ) : (
      <div className="foreign-travel-questions">
        <Field
          attribute={`${attribute}.Country`}
          title={i18n.t('foreign.travel.heading.country')}
          adjustFor="country"
          scrollIntoView={this.props.scrollIntoView}
        >
          <Country
            attribute={`${attribute}.Country`}
            name="Country"
            {...this.props.Country}
            excludeUnitedStates
            className="foreign-travel-country"
            onUpdate={this.updateCountry}
            updateCountryFocused={this.updateCountryFocused}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('foreign.travel.heading.dates')}
          help="foreign.travel.help.dates"
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Dates`}
        >
          <DateRange
            attribute={`${attribute}.Dates`}
            name="Dates"
            {...this.props.Dates}
            className="foreign-travel-dates"
            minDateEqualTo
            onUpdate={this.updateDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Field
          attribute={`${attribute}.Days`}
          title={i18n.t('foreign.travel.heading.days')}
          help="foreign.travel.help.days"
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
        >
          <TravelDays
            attribute={`${attribute}.Days`}
            name="Days"
            className="foreign-travel-days"
            {...this.props.Days}
            onUpdate={this.updateDays}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>

        <Field
          attribute={`${attribute}.Purpose`}
          title={i18n.t('foreign.travel.heading.purpose')}
          adjustFor="p"
          scrollIntoView={this.props.scrollIntoView}
        >
          {i18n.m('foreign.travel.para.checkall')}
          <TravelPurpose
            attribute={`${attribute}.Purpose`}
            name="Purpose"
            className="foreign-travel-purpose"
            {...this.props.Purpose}
            onUpdate={this.updatePurpose}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        <Branch
          attribute={`${attribute}.Questioned`}
          label={i18n.t('foreign.travel.heading.questioned')}
          labelSize="h4"
          name="has_foreign_travel_questioned"
          className={`foreign-travel-questioned ${(this.props.Questioned || {}).value === 'Yes' ? 'no-margin-bottom' : ''
            }`}
          {...this.props.Questioned}
          onUpdate={this.updateQuestioned}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Show when={(this.props.Questioned || {}).value === 'Yes'}>
          <Field
            attribute={`${attribute}.QuestionedExplanation`}
            title={i18n.t('foreign.travel.heading.explanation')}
            titleSize="label"
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              attribute={`${attribute}.QuestionedExplanation`}
              name="QuestionedExplanation"
              {...this.props.QuestionedExplanation}
              className="foreign-travel-questioned-explanation"
              onUpdate={this.updateQuestionedExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>

        <Branch
          attribute={`${attribute}.Encounter`}
          label={i18n.t('foreign.travel.heading.encounter')}
          labelSize="h4"
          name="has_foreign_travel_encounter"
          className={`foreign-travel-encounter ${this.props.Encounter.value === 'Yes' ? 'no-margin-bottom' : ''
            }`}
          {...this.props.Encounter}
          onUpdate={this.updateEncounter}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Show when={(this.props.Encounter || {}).value === 'Yes'}>
          <Field
            attribute={`${attribute}.EncounterExplanation`}
            title={i18n.t('foreign.travel.heading.explanation')}
            titleSize="label"
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              attribute={`${attribute}.EncounterExplanation`}
              name="EncounterExplanation"
              {...this.props.EncounterExplanation}
              className="foreign-travel-encounter-explanation"
              onUpdate={this.updateEncounterExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>

        <Branch
          attribute={`${attribute}.Contacted`}
          label={i18n.t('foreign.travel.heading.contacted')}
          labelSize="h4"
          name="has_foreign_travel_contacted"
          className={`foreign-travel-contacted ${this.props.Contacted.value === 'Yes' ? 'no-margin-bottom' : ''
            }`}
          {...this.props.Contacted}
          onUpdate={this.updateContacted}
          required={this.props.required}
          onError={this.props.onError}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Show when={(this.props.Contacted || {}).value === 'Yes'}>
          <Field
            attribute={`${attribute}.ContactedExplanation`}
            title={i18n.t('foreign.travel.heading.explanation')}
            titleSize="label"
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              name="ContactedExplanation"
              attribute={`${attribute}.ContactedExplanation`}
              {...this.props.ContactedExplanation}
              className="foreign-travel-contacted-explanation"
              onUpdate={this.updateContactedExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </Show>

        {requireForeignCounterIntelligence && (
          <Branch
            attribute={`${attribute}.Counter`}
            label={i18n.t('foreign.travel.heading.counter')}
            labelSize="h4"
            name="has_foreign_travel_counter"
            className={`foreign-travel-counter ${this.props.Counter.value === 'Yes' ? 'no-margin-bottom' : ''
              }`}
            {...this.props.Counter}
            onUpdate={this.updateCounter}
            required={this.props.required}
            onError={this.props.onError}
          />
        )}
        {requireForeignCounterIntelligence && (
          <Show when={(this.props.Counter || {}).value === 'Yes'}>
            <Field
              attribute={`${attribute}.CounterExplanation`}
              title={i18n.t('foreign.travel.heading.explanation')}
              titleSize="label"
              adjustFor="textarea"
            >
              <Textarea
                attribute={`${attribute}.CounterExplanation`}
                name="CounterExplanation"
                {...this.props.CounterExplanation}
                className="foreign-travel-counter-explanation"
                onUpdate={this.updateCounterExplanation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          </Show>
        )}

        {requireForeignExcessiveKnowledge && (
          <Branch
            attribute={`${attribute}.Interest`}
            label={i18n.t('foreign.travel.heading.interest')}
            labelSize="h4"
            name="has_foreign_travel_interest"
            className={`foreign-travel-interest ${this.props.Interest.value === 'Yes' ? 'no-margin-bottom' : ''
              }`}
            {...this.props.Interest}
            onUpdate={this.updateInterest}
            required={this.props.required}
            onError={this.props.onError}
          />
        )}
        {requireForeignExcessiveKnowledge && (
          <Show when={(this.props.Interest || {}).value === 'Yes'}>
            <Field
              attribute={`${attribute}.InterestExplanation`}
              title={i18n.t('foreign.travel.heading.explanation')}
              titleSize="label"
              adjustFor="textarea"
            >
              <Textarea
                attribute={`${attribute}.InterestExplanation`}
                name="InterestExplanation"
                {...this.props.InterestExplanation}
                className="foreign-travel-interest-explanation"
                onUpdate={this.updateInterestExplanation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          </Show>
        )}

        {requireForeignSensitiveInformation && (
          <Branch
            attribute={`${attribute}.Sensitive`}
            label={i18n.t('foreign.travel.heading.sensitive')}
            labelSize="h4"
            name="has_foreign_travel_sensitive"
            className={`foreign-travel-sensitive ${this.props.Sensitive.value === 'Yes' ? 'no-margin-bottom' : ''
              }`}
            {...this.props.Sensitive}
            onUpdate={this.updateSensitive}
            required={this.props.required}
            onError={this.props.onError}
          />
        )}
        {requireForeignSensitiveInformation && (
          <Show when={(this.props.Sensitive || {}).value === 'Yes'}>
            <Field
              attribute={`${attribute}.SensitiveExplanation`}
              title={i18n.t('foreign.travel.heading.explanation')}
              titleSize="label"
              adjustFor="textarea"
            >
              <Textarea
                attribute={`${attribute}.SensitiveExplanation`}
                name="SensitiveExplanation"
                {...this.props.SensitiveExplanation}
                className="foreign-travel-sensitive-explanation"
                onUpdate={this.updateSensitiveExplanation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          </Show>
        )}

        {requireForeignThreatened && (
          <Branch
            attribute={`${attribute}.Threatened`}
            label={i18n.t('foreign.travel.heading.threatened')}
            labelSize="h4"
            name="has_foreign_travel_threatened"
            className={`foreign-travel-threatened ${this.props.Threatened.value === 'Yes' ? 'no-margin-bottom' : ''
              }`}
            {...this.props.Threatened}
            onUpdate={this.updateThreatened}
            required={this.props.required}
            onError={this.props.onError}
          />
        )}
        {requireForeignThreatened && (
          <Show when={(this.props.Threatened || {}).value === 'Yes'}>
            <Field
              attribute={`${attribute}.ThreatenedExplanation`}
              title={i18n.t('foreign.travel.heading.explanation')}
              titleSize="label"
              adjustFor="textarea"
            >
              <Textarea
                attribute={`${attribute}.ThreatenedExplanation`}
                name="ThreatenedExplanation"
                {...this.props.ThreatenedExplanation}
                className="foreign-travel-threatened-explanation"
                onUpdate={this.updateThreatenedExplanation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          </Show>
        )}
      </div>
    );
  }
}

TravelQuestions.defaultProps = {
  Dates: {},
  Country: {},
  Days: [],
  Purpose: {},
  PurposeExplanation: {},
  Questioned: {},
  QuestionedExplanation: {},
  Encounter: {},
  EncounterExplanation: {},
  Contacted: {},
  ContactedExplanation: {},
  Counter: {},
  CounterExplanation: {},
  Interest: {},
  InterestExplanation: {},
  Sensitive: {},
  SensitiveExplanation: {},
  Threatened: {},
  ThreatenedExplanation: {},
  requireForeignCounterIntelligence: true,
  requireForeignExcessiveKnowledge: true,
  requireForeignSensitiveInformation: true,
  requireForeignThreatened: true,
  CountryFocused: false,
  onUpdate: () => { },
  onError: (value, arr) => arr,
};
